/* eslint-disable jsx-a11y/mouse-events-have-key-events */
/* eslint-disable react/jsx-no-bind */
import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { push } from 'connected-react-router';
import { draggable, dropTargetForElements, monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Button from '@material-ui/core/Button';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { fetchDataDetail } from '@actions/data-detail-actions';
import { saveDataType } from '@actions/data-types-actions';
import { pushApplicationMessage } from '@actions/messages-actions';
import { reorderCustomFields } from '@actions/workflow-actions';
import * as colors from '@constants/colors';
import { getWorkflowCustomFieldSections } from '@constants/config';
import { sectionTooltips } from '@constants/workflow';
import SwitchControl from '@shared/form/switch-control';
import { getTaskFieldIcon } from '@utils/icon-utils';
import {
  getFieldsMaxOrder,
  getTaskFields,
  getTaskSectionTitleFieldName
} from '@utils/workflow-utils';
import FieldHelpIcon from './field-help-icon';
import './details-layout-drawer.scss';

// Button to add new fields.
const AddButton = ({ id, onAdd, order }) => (
  <div data-testid="task-type-drawer-add-button" styleName="section-row row" key={`add-button-${id}`}>
    <Button color="primary" onClick={() => onAdd(id, order)} startIcon={<AddIcon />}>ADD FIELD</Button>
  </div>
);

AddButton.propTypes = {
  id: PropTypes.number,
  onAdd: PropTypes.func,
  order: PropTypes.number
};

const FieldActions = ({ field, hovered, onDelete, onEdit }) => {
  if (!field.core && hovered) {
    const iconProps = { htmlColor: '#616161', style: { fontSize: '1.25rem', padding: '0 0 0 0.5rem' } };
    return (
      <div styleName="row-action">
        <EditIcon {...iconProps} onClick={() => onEdit(field)} />
        <DeleteIcon {...iconProps} onClick={() => onDelete(field)} />
      </div>
    );
  }
  return field.required ? 'Required' : 'Optional';
};

FieldActions.propTypes = {
  field: PropTypes.object,
  hovered: PropTypes.bool,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func
};

// Renders a draggable field row.
const DraggableField = ({ field, index, sectionId, onDelete, onEdit }) => {
  const [hovered, setHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const rowRef = useRef(null);
  const dragHandleRef = useRef(null);

  const hoverOn = () => setHovered(true);
  const hoverOff = () => setHovered(false);
  const isDraggable = !field.core && field.id;

  // Set up draggable
  useEffect(() => {
    const element = rowRef.current;
    const dragHandle = dragHandleRef.current;

    if (!element || !isDraggable) {
      return () => {};
    }

    return draggable({
      element,
      dragHandle: dragHandle || element,
      getInitialData: () => ({ index, field, sectionId, type: 'task-type-field' }),
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        // Using native drag preview
        const { setCustomNativeDragPreview } = require('@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview');
        const { pointerOutsideOfPreview } = require('@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview');
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),  // eslint-disable-line id-length
          render: ({ container }) => {
            const preview = document.createElement('div');
            preview.style.cssText = `
              padding: 0.75rem 1.5rem;
              background-color: white;
              border: 1px solid ${colors.dotmapsGrayBorder};
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.15);
              font-size: 0.875rem;
              font-family: Roboto, sans-serif;
            `;
            preview.textContent = field.label || field.name;
            container.appendChild(preview);
            return () => container.removeChild(preview);
          }
        });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false)
    });
  }, [index, field, sectionId, isDraggable]);

  // Set up drop target on each row
  useEffect(() => {
    const element = rowRef.current;
    if (!element || !isDraggable) {
      return () => {};
    }

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => source.data.type === 'task-type-field' && source.data.sectionId === sectionId,
      getData: ({ input, element: el }) => {
        return attachClosestEdge({ index, sectionId }, {
          input,
          element: el,
          allowedEdges: ['top', 'bottom']
        });
      },
      onDragEnter: ({ self }) => {
        setIsDraggedOver(true);
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDrag: ({ self }) => {
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDragLeave: () => {
        setIsDraggedOver(false);
        setClosestEdge(null);
      },
      onDrop: () => {
        setIsDraggedOver(false);
        setClosestEdge(null);
      }
    });
  }, [index, sectionId, isDraggable]);

  return (
    <div
      ref={rowRef}
      onMouseEnter={hoverOn}
      onMouseLeave={hoverOff}
      styleName="section-row row field"
      style={{
        border: isDragging ? `1px solid ${colors.dotmapsGrayBorder}` : 'none',
        opacity: isDragging ? 0.4 : 1,
        width: '100%',
        position: 'relative'
      }}
    >
      {/* Drop indicator line */}
      {isDraggedOver && closestEdge === 'top' && (
        <div style={{
          position: 'absolute',
          top: -1,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: colors.dotmapsBlue,
          zIndex: 1
        }} />
      )}
      {isDraggedOver && closestEdge === 'bottom' && (
        <div style={{
          position: 'absolute',
          bottom: -1,
          left: 0,
          right: 0,
          height: '2px',
          backgroundColor: colors.dotmapsBlue,
          zIndex: 1
        }} />
      )}
      <div styleName="row-left field-container">
        {isDraggable && (
          <div
            ref={dragHandleRef}
            data-testid="drag-handle"
            styleName="drag-handle"
            style={{ cursor: 'grab' }}
          >
            <DragIndicatorIcon htmlColor="#999999" style={{ fontSize: '1rem', width: '1.5rem' }} />
          </div>
        )}
        <div styleName="field-icon">
          {getTaskFieldIcon(field)}
        </div>
        <div styleName="field-label">
          {field.label || field.name}
        </div>
      </div>
      <div styleName="row-right second-text">
        <FieldActions field={field} hovered={hovered} onDelete={onDelete} onEdit={onEdit} />
      </div>
    </div>
  );
};

DraggableField.propTypes = {
  field: PropTypes.object,
  index: PropTypes.number,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  sectionId: PropTypes.number
};

// Non-draggable field (core fields)
const CoreField = ({ field, onDelete, onEdit }) => {
  const [hovered, setHovered] = useState(false);
  const hoverOn = () => setHovered(true);
  const hoverOff = () => setHovered(false);

  return (
    <div
      onMouseEnter={hoverOn}
      onMouseLeave={hoverOff}
      styleName="section-row row field"
      style={{ width: '100%' }}
    >
      <div styleName="row-left field-container">
        <div styleName="field-icon">
          {getTaskFieldIcon(field)}
        </div>
        <div styleName="field-label">
          {field.label || field.name}
        </div>
      </div>
      <div styleName="row-right second-text">
        <FieldActions field={field} hovered={hovered} onDelete={onDelete} onEdit={onEdit} />
      </div>
    </div>
  );
};

CoreField.propTypes = {
  field: PropTypes.object,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func
};

// Lists each section (group of fields, i.e. Information fields, Response fields) and
// its list of fields.
const TaskTypeDrawerList = ({ onAdd, onDelete, onEdit }) => {
  const { dataType } = useParams();
  const { data, loading } = useSelector(state => state.dataDetail);
  const dispatch = useDispatch();
  const [droppedField, setDroppedField] = useState(null);  // Last moved (dropped) field.
  const [isDragging, setIsDragging] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(null);

  // Trick to show the drag-and-dropped field in the new position before we finish reloading
  // the task template's fields.
  useEffect(() => {
    if (loading === false) {
      setDroppedField(null);
    }
  }, [loading]);

  const sections = getWorkflowCustomFieldSections();

  // Monitor for drag events
  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => source.data.type === 'task-type-field',
      onDragStart: ({ source }) => {
        setIsDragging(true);
        setActiveSectionId(source.data.sectionId);
      },
      onDrop: ({ source, location }) => {
        setIsDragging(false);
        setActiveSectionId(null);

        const destination = location.current.dropTargets[0];
        if (!destination) {
          return;
        }

        const sourceIndex = source.data.index;
        const sourceSectionId = source.data.sectionId;
        const destinationIndex = destination.data.index;
        const destinationSectionId = destination.data.sectionId;

        // Only allow reordering within the same section
        if (sourceSectionId !== destinationSectionId) {
          return;
        }

        const edge = extractClosestEdge(destination.data);

        // Calculate the new order
        let newIndex = destinationIndex;
        if (edge === 'bottom') {
          newIndex = destinationIndex + 1;
        }

        // Adjust if moving down
        if (sourceIndex < newIndex) {
          newIndex -= 1;
        }

        // Don't do anything if dropped in the same position
        if (sourceIndex === newIndex) {
          return;
        }

        const field = source.data.field;
        const new_order = newIndex + 1;  // 1-based order
        setDroppedField({ ...field, order: new_order });

        const payload = {
          id: field.id,
          section: field.section,
          new_order,
          order: field.order,
          task_type: field.task_type
        };

        // Call backend to save the new order:
        dispatch(reorderCustomFields(payload));
      }
    });
  }, [dispatch]);

  if (!data) {
    return null;
  }

  const toggleResponse = value => {
    const callback = ({ id }) => {
      dispatch(fetchDataDetail(dataType, data.id));
      const successMessage = value ? 'Response fields are enabled.' : 'Response fields are disabled.';
      dispatch(pushApplicationMessage(successMessage));
      // If the id changed (due to versioning) redirect.
      if (String(data.id) !== String(id)) {
        dispatch(push({
          pathname: `/admin/task_type/${id}`,
          state: { clear: true }
        }));
      }
    };

    const payload = { response: value };
    dispatch(saveDataType(dataType, data.id, payload, callback));
  };

  return (
    <Fragment>
      <div data-testid="task-type-drawer-list-header" styleName="header row">
        <div data-testid="task-type-drawer-list-header-title" styleName="row-left">Task fields</div>
        <div data-testid="task-type-drawer-list-header-switch" styleName="row-right">
          <SwitchControl label="Response fields enabled" labelPlacement="start" onChange={toggleResponse} value={data.response} />
        </div>
      </div>
      {sections.map(section => {
        const title = getTaskSectionTitleFieldName(section);
        const fields = getTaskFields(section, data, droppedField, true);
        const coreFields = fields.filter(field => field.core || !field.id);
        const normalFields = fields.filter(field => !field.core && field.id);

        // If the response fields are not enabled, don't render that section:
        if (section.name === 'Response' && !data.response) {
          return null;
        }

        const isActiveSection = isDragging && activeSectionId === section.id;

        return (
          <Fragment key={section.id}>
            <div
              data-testid="task-type-drawer-list-section-title"
              styleName="section-title row"
            >
              {title}
              <FieldHelpIcon>
                {sectionTooltips[section.id]}
              </FieldHelpIcon>
            </div>
            {/* Core fields (non-draggable) */}
            <div
              data-testid="task-type-drawer-list-droppable-1"
              style={{
                border: '2px solid white',
                backgroundColor: colors.overlayTextColor,
                boxSizing: 'border-box',
                width: '100%'
              }}
            >
              {coreFields.map((field, index) => (
                <CoreField
                  field={field}
                  key={field.id || `core-${index}`}
                  onDelete={onDelete}
                  onEdit={onEdit}
                />
              ))}
            </div>
            {/* Normal fields (draggable) */}
            <div
              data-testid="task-type-drawer-list-droppable-2"
              style={{
                border: isActiveSection ? `2px dashed ${colors.dotmapsBlue}` : '2px solid white',
                backgroundColor: isActiveSection ? '#E9F0FD' : colors.overlayTextColor,
                boxSizing: 'border-box',
                width: '100%'
              }}
            >
              {normalFields.map((field, index) => (
                <DraggableField
                  field={field}
                  index={index}
                  key={field.id}
                  onDelete={onDelete}
                  onEdit={onEdit}
                  sectionId={section.id}
                />
              ))}
            </div>
            {(section.name === 'Response' || section.name === 'Information') && (
              <AddButton id={section.id} onAdd={onAdd} order={getFieldsMaxOrder(fields)} />
            )}
          </Fragment>
        );
      })}
    </Fragment>
  );
};

TaskTypeDrawerList.propTypes = {
  onAdd: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func
};

export default TaskTypeDrawerList;
